import React, { Dispatch, useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import LaunchIcon from '@mui/icons-material/Launch';
import TreeView from '@mui/lab/TreeView';
import { createStyles, Theme } from '@mui/material/styles';
import { withStyles, makeStyles } from '@mui/styles';
import TreeItem, { TreeItemProps } from '@mui/lab/TreeItem';
import { TransitionProps } from '@mui/material/transitions';
import { animated, useSpring } from 'react-spring';
import Collapse from '@mui/material/Collapse';
import { ItemTypes } from '../../types/ItemTypes';
import { ReactComponent as Point } from '../../assets/images/treePoint.svg';
import style from './DnDTree.module.scss';
import { ReactComponent as Eye } from '../../assets/images/eye-off.svg';
import { RenderTreeItem } from './Item';
import { Subdivision } from '../../types/subdivision';
import { useOrganization } from '../../hooks/entityHooks';
import { Organization } from '../../types/organization';
import { SearchTree } from '../../types/searchTree';
import { ReactComponent as EyeOpen } from '../../assets/images/eye.svg';

const StyledTreeItem = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    iconContainer: {
      marginBottom: 26,
      '& .close': {
        opacity: 0.3,
      },
    },
    group: {
      marginLeft: 7,

      paddingLeft: 18,
    },
    label: {
      marginBottom: 26,
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 16,
      // color: '#444A52',
      width: '100%',
      color: '#FF7221',
    },
  }),
)((props: TreeItemProps) => (
  <TreeItem {...props} TransitionComponent={TransitionComponent} />
));

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    from: { opacity: 0, transform: 'translate3d(20px,0,0)' },
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

const useStyles = makeStyles(
  createStyles({
    root: {
      flexGrow: 1,
      width: '100%',
    },

    elemenent: {
      flexGrow: 1,
      width: 25,
    },
  }),
);

const backUseStyle = makeStyles(
  createStyles({
    root: {
      backgroundColor: 'rgba(235, 86, 79, 0.2)',
    },
  }),
);
type handleProps = {
  id: number;
  type: string;
  handleGetTree?(): void;
};

type ChangeOptions = {
  a: string;
  b: string;
};
type logsInfo = {
  id?: number;
  type?: number;
};
type UpdateEntity = {
  id: number;
  getTree(): void;
  type: number;
};
type dndProps = {
  setEmployeeInfoShow: Dispatch<boolean>;
  setOrgInfoShow: Dispatch<boolean>;
  element: Organization | SearchTree;
  handleSetStaff({ id, type }: logsInfo): void;
  handleOrganizationInfo({ id, type, handleGetTree }: handleProps): void;
  // tree: Subdivision[];
  clicked: number[];
  // getEntity(id: number): void;
  setClicked(array: number[]): void;
  handleChangeOptions({ a, b }: ChangeOptions): void;
  handleChangeIsKarmaUser(
    id: number,
    type: number,
    value: boolean,
    handleGetTree: () => void,
  ): void;
  handleSetEntityLoader(val: boolean): void;
  entityLoader: boolean;
  handleSetEntityUpdateArray({ id, getTree, type }: UpdateEntity): void;
  getEntityList(search?: URLSearchParams): Promise<void>;
  showOrganization: boolean;
  setShowOrganization: (flag: boolean) => void;
};

export const ParentElement = memo(({
  element,
  handleOrganizationInfo,
  handleSetStaff,
  setEmployeeInfoShow,
  setOrgInfoShow,
  clicked,
  setClicked,
  handleChangeOptions,
  handleChangeIsKarmaUser,
  handleSetEntityLoader,
  entityLoader,
  handleSetEntityUpdateArray,
  getEntityList,
  showOrganization,
  setShowOrganization,
}: dndProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [isSearch, setIseSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { getTree, entity: tree } = useOrganization();

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: ItemTypes.SUBDIVISION,
    drop: () => ({ name: `${element.orgShortName}` }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  
  useEffect(() => {
    handleSetEntityUpdateArray({
      id: element.id,
      getTree: getEntityList,
      type: 1,
    });
  }, [element.id, getEntityList, handleSetEntityUpdateArray]);
  
  const isActive = canDrop && isOver;

  const backStyle = backUseStyle();
  const [expandArray, setExpandArray] = useState<string[]>([]);
  const expandSetRef = useRef<Set<string>>(new Set());

  const fillExpandArray = React.useCallback((el: Subdivision[]): string[] => {
    const resultSet = new Set<string>();
    
    const traverse = (subdivisions: Subdivision[]) => {
      for (const elem of subdivisions) {
        const idStr = elem.id.toString();
        if (!resultSet.has(idStr)) {
          resultSet.add(idStr);
        }
        if (elem.subdivisions && elem.subdivisions.length > 0) {
          traverse(elem.subdivisions);
        }
      }
    };
    
    traverse(el);
    return Array.from(resultSet);
  }, []);

  const loadTree = async (page: number = 1, limit: number = 40) => {
    handleSetEntityLoader(true);
    await getTree(element.id, page, limit);
    handleSetEntityLoader(false);
  };
  
  useEffect(() => {
    if (element.subdivisionsParent && !loading && !expandArray.length) {
      // Используем requestIdleCallback для неблокирующей обработки больших деревьев
      const processExpandArray = () => {
        const elementId = element.id.toString();
        const newExpandArray = [elementId];
        const filledArray = fillExpandArray(element.subdivisionsParent!);
        const combined = [...newExpandArray, ...filledArray];
        
        // Обновляем Set для быстрого поиска
        expandSetRef.current = new Set(combined);
        setExpandArray(combined);
        setLoading(true);
        setIseSearch(true);
        loadTree();
      };

      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(processExpandArray, { timeout: 1000 });
      } else {
        // Fallback для браузеров без requestIdleCallback
        setTimeout(processExpandArray, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.subdivisionsParent, element.id, fillExpandArray]);

  const getNewSubdivision = async (page: number = 1, limit: number = 40) => {
    await getTree(element.id, page, limit);
  };

  const handleTreeItemClick = useCallback(() => {
    const isAlreadyClicked = clicked.includes(element.id);
    if (!isAlreadyClicked && !element.subdivisionsParent) {
      // Используем requestAnimationFrame для неблокирующего выполнения
      // Загружаем первую страницу с лимитом 40 для быстрой загрузки
      requestAnimationFrame(() => {
        handleSetEntityLoader(true);
        getTree(element.id, 1, 40).then(() => {
          handleSetEntityLoader(false);
        }).catch(() => {
          handleSetEntityLoader(false);
        });
        setClicked([...clicked, element.id]);
      });
    }
    if (element.orgName === 'Группа ТМК') {
      getEntityList();
      setShowOrganization(!showOrganization);
    }
  }, [clicked, element.id, element.subdivisionsParent, element.orgName, getTree, handleSetEntityLoader, setClicked, getEntityList, setShowOrganization, showOrganization]);

  // Используем контролируемый режим для правильного обновления дерева
  // Мемоизируем начальное состояние expanded
  const initialExpanded = useMemo(() => {
    return expandArray.length ? expandArray : [element.id.toString()];
  }, [element.id]);

  const [expanded, setExpanded] = useState<string[]>(initialExpanded);

  // Синхронизируем expanded с expandArray при его изменении
  // Используем useRef для отслеживания предыдущего значения, чтобы избежать лишних обновлений
  const prevExpandArrayRef = useRef<string[]>(expandArray);
  
  useEffect(() => {
    // Проверяем, действительно ли изменился expandArray
    const currentSet = new Set(expandArray);
    const prevSet = expandSetRef.current;
    
    // Проверяем, есть ли новые элементы
    const hasNewItems = expandArray.some(id => !prevSet.has(id));
    
    if (hasNewItems) {
      prevExpandArrayRef.current = expandArray;
      expandSetRef.current = currentSet;
      
      // Используем requestAnimationFrame для плавного обновления без блокировки UI
      requestAnimationFrame(() => {
        setExpanded((prev) => {
          const prevSet = new Set(prev);
          // Объединяем старые и новые значения
          expandArray.forEach(id => prevSet.add(id));
          const newExpanded = Array.from(prevSet);
          return newExpanded;
        });
      });
    }
  }, [expandArray]);

  const handleNodeToggle = useCallback((event: React.SyntheticEvent, nodeIds: string[]) => {
    // Обновляем ref при изменении expanded пользователем
    expandSetRef.current = new Set(nodeIds);
    setExpanded(nodeIds);
  }, []);

  return (
    <>
      <TreeView
        className={classes.root}
        defaultCollapseIcon={<Point />}
        expanded={expanded}
        onNodeToggle={handleNodeToggle}
      >
        <div className={style.row}>
          <LaunchIcon
            fontSize="small"
            onClick={() => {
              handleOrganizationInfo({
                type: 'Организация',
                id: element.id,
                handleGetTree: getEntityList,
              });
              handleSetStaff({ id: element.id, type: 1 });
              setOrgInfoShow(true);
              setEmployeeInfoShow(false);
            }}
            color="secondary"
            className={style.row__pointer}
          />
          <StyledTreeItem
            className={isActive ? backStyle.root : ''}
            nodeId={element.id.toString()}
            onClick={handleTreeItemClick}
            label={
              <div
                className={style.label2}
                ref={drop}
                style={{ paddingRight: 24 }}
              >
                <div className={style.label}>
                  <div>{element.orgShortName}</div>
                  &nbsp;
                  <div style={{ color: '#CACED2' }}>
                    {element.staffsCount ? ` (${element.staffsCount})` : '(0)'}
                  </div>
                </div>
              </div>
            }
          >
            {tree && tree.subdivisionsParent ? (
              <>
                <RenderTreeItem
                  isSearch={isSearch}
                  setOrgInfoShow={setOrgInfoShow}
                  setEmployeeInfoShow={setEmployeeInfoShow}
                  tree={tree.subdivisionsParent}
                  branch={[
                    tree.id,
                    ...tree.subdivisionsParent.map(
                      (subdivision) => subdivision.id,
                    ),
                  ]}
                  org={tree}
                  handleSetStaff={handleSetStaff}
                  handleOrganizationInfo={handleOrganizationInfo}
                  previousName={tree.orgShortName}
                  handleChangeOptions={handleChangeOptions}
                  handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                  expandArray={expandArray}
                  entityLoader={entityLoader}
                  handleSetEntityLoader={handleSetEntityLoader}
                  handleGetTree={getNewSubdivision}
                  handleSetEntityUpdateArray={handleSetEntityUpdateArray}
                  loadMoreRef={loadMoreRef}
                />
                {isLoadingMore && (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                    Загрузка...
                  </div>
                )}
              </>
            ) : null}
          </StyledTreeItem>
          <span
            style={{
              minWidth: 24,
              position: 'absolute',
              right: 0,
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                display: element.orgName === 'Группа ТМК' ? 'none' : 'block',
              }}
            >
              {element.isKarmaUser ? (
                <EyeOpen
                  onClick={async () => {
                    if (getNewSubdivision) {
                      await handleChangeIsKarmaUser(
                        element.id,
                        1,
                        !element.isKarmaUser,
                        getNewSubdivision,
                      );
                      await getEntityList();
                      await getTree(element.id);
                    }
                  }}
                />
              ) : (
                <Eye
                  stroke="#CACED2"
                  onClick={async () => {
                    if (getNewSubdivision) {
                      await handleChangeIsKarmaUser(
                        element.id,
                        1,
                        !element.isKarmaUser,
                        getNewSubdivision,
                      );
                      await getEntityList();
                      await getTree(element.id);
                    }
                  }}
                />
              )}
              {/*
              {tree ? (
                tree.karmaUser ? (
                  <EyeOpen
                    onClick={async () => {
                      if (getNewSubdivision) {
                        await handleChangeIsKarmaUser(
                          tree.id,
                          1,
                          !tree.karmaUser,
                          getNewSubdivision,
                        );
                      }
                    }}
                  />
                ) : (
                  <Eye
                    stroke="#CACED2"
                    onClick={async () => {
                      if (getNewSubdivision) {
                        await handleChangeIsKarmaUser(
                          tree.id,
                          1,
                          !tree.karmaUser,
                          getNewSubdivision,
                        );
                        await getTree(element.id);
                      }
                    }}
                  />
                )
              ) : element.isKarmaUser ? (
                <EyeOpen
                  onClick={async () => {
                    if (getNewSubdivision) {
                      await handleChangeIsKarmaUser(
                        element.id,
                        1,
                        !element.isKarmaUser,
                        getNewSubdivision,
                      );
                      await getTree(element.id);
                    }
                  }}
                />
              ) : (
                <Eye
                  stroke="#CACED2"
                  onClick={async () => {
                    if (getNewSubdivision) {
                      await handleChangeIsKarmaUser(
                        element.id,
                        1,
                        !element.isKarmaUser,
                        getNewSubdivision,
                      );
                      await getTree(element.id);
                    }
                  }}
                />
              )}
*/}
            </span>
          </span>
        </div>
      </TreeView>
    </>
  );
});
